/**
 * Add External Service Dialog Component
 * Allows adding external services (done outside Thaklis) to invoices
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Grid,
  Typography,
  Box,
  Divider,
  Alert,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import ApiService from '../services/api.service';
import internalServiceService from '../services/internal-service.service';

const TAX_RATE = 0.05; // 5% VAT on service charge

const AddExternalServiceDialog = ({ open, onClose, invoiceId, onSuccess, thaklisDisabled = false, organizationId = null }) => {
  // Form state. Types: THAKLIS (hidden when Thaklis disabled), CATALOG (internal services resolved for
  // the invoice's org), CUSTOM (free-form). Default to Thaklis when enabled, else Catalog.
  const [serviceType, setServiceType] = useState(thaklisDisabled ? 'CATALOG' : 'THAKLIS');
  const [selectedServiceGroup, setSelectedServiceGroup] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [customServiceName, setCustomServiceName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [transactionNumber, setTransactionNumber] = useState('');
  const [govtCharge, setGovtCharge] = useState('');
  const [serviceCharge, setServiceCharge] = useState('');
  const [serviceChargeExclTax, setServiceChargeExclTax] = useState('');
  const [serviceChargeTax, setServiceChargeTax] = useState('');
  const [reference, setReference] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [description, setDescription] = useState('');

  // Data state
  const [serviceGroups, setServiceGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  // Catalog (internal) services resolved for the invoice's organization.
  const [catalogServices, setCatalogServices] = useState([]);
  const [selectedCatalogService, setSelectedCatalogService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Calculated values (external services only; quantity multiplies the per-unit total)
  const qtyNum = Math.max(1, parseInt(quantity, 10) || 1);
  const unitTotal = (parseFloat(govtCharge) || 0) + (parseFloat(serviceCharge) || 0);
  const total = unitTotal * qtyNum;

  // Fetch service groups on mount
  useEffect(() => {
    if (open) {
      if (!thaklisDisabled) {
        fetchServiceGroups();
        fetchServices();
      }
      // Catalog (internal) services resolved for the invoice's org (walk-in / no org → uniform only).
      internalServiceService.getServicesForOrganization(organizationId)
        .then(setCatalogServices)
        .catch(() => setCatalogServices([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationId, thaklisDisabled]);

  // Filter services when group changes
  // Note: Many services don't have serviceGroup populated, so we show all services
  // and use the service name matching if possible
  useEffect(() => {
    if (selectedServiceGroup && selectedServiceGroup.name) {
      // Try to filter by serviceGroup.id first
      let filtered = services.filter(s => s.serviceGroup?.id === selectedServiceGroup.id);

      // If no services found with serviceGroup, try matching by name pattern
      if (filtered.length === 0) {
        const groupNameLower = selectedServiceGroup.name.toLowerCase();
        filtered = services.filter(s => {
          const serviceName = (s.name || '').toLowerCase();
          const serviceDesc = (s.description || '').toLowerCase();
          // Match if service name or description contains the group name
          return serviceName.includes(groupNameLower) || serviceDesc.includes(groupNameLower);
        });
      }

      // If still no matches, show all services
      setFilteredServices(filtered.length > 0 ? filtered : services);
    } else {
      setFilteredServices(services);
    }
  }, [selectedServiceGroup, services]);

  // Auto-fill charges when Thaklis service is selected
  useEffect(() => {
    if (serviceType === 'THAKLIS' && selectedService) {
      setGovtCharge(selectedService.govtCharge?.toString() || '0');
      const excl = selectedService.serviceCharge ?? 0;
      const tax = selectedService.taxOnServiceCharge ?? (excl * TAX_RATE);
      const incl = excl + tax;

      setServiceChargeExclTax(Number(excl).toFixed(2));
      setServiceChargeTax(Number(tax).toFixed(2));
      setServiceCharge(incl.toFixed(2));
    }
  }, [selectedService, serviceType]);

  // Auto-fill charges from the selected catalog (internal) service — resolved for the org.
  useEffect(() => {
    if (serviceType === 'CATALOG' && selectedCatalogService) {
      const excl = Number(selectedCatalogService.serviceCharge ?? selectedCatalogService.service_charge ?? 0);
      const tax = Number(selectedCatalogService.taxOnServiceCharge ?? selectedCatalogService.tax_on_service_charge ?? 0);
      const govt = Number(selectedCatalogService.govtCharge ?? selectedCatalogService.govt_charge ?? 0);
      setGovtCharge(govt ? govt.toString() : '0');
      setServiceChargeExclTax(excl.toFixed(2));
      setServiceChargeTax(tax.toFixed(2));
      setServiceCharge((excl + tax).toFixed(2));
    }
  }, [selectedCatalogService, serviceType]);

  const fetchServiceGroups = async () => {
    try {
      const response = await ApiService.getServiceGroups();
      if (response.data.success) {
        setServiceGroups(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching service groups:', err);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getServices();
      if (response.data.success) {
        setServices(response.data.data || []);
        setFilteredServices(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setServiceType(thaklisDisabled ? 'CATALOG' : 'THAKLIS');
    setSelectedServiceGroup(null);
    setSelectedService(null);
    setSelectedCatalogService(null);
    setCustomServiceName('');
    setCustomerName('');
    setTransactionNumber('');
    setGovtCharge('');
    setServiceCharge('');
    setServiceChargeExclTax('');
    setServiceChargeTax('');
    setReference('');
    setQuantity('1');
    setDescription('');
    setError(null);
  }, [thaklisDisabled]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleServiceTypeChange = (event) => {
    setServiceType(event.target.value);
    setSelectedService(null);
    setSelectedCatalogService(null);
    setCustomServiceName('');
    // Clear charges when switching to CUSTOM/CATALOG (CATALOG re-fills once a service is picked).
    if (event.target.value === 'CUSTOM' || event.target.value === 'CATALOG') {
      setGovtCharge('');
      setServiceCharge('');
      setServiceChargeExclTax('');
      setServiceChargeTax('');
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (serviceType === 'THAKLIS' && !selectedService) {
      setError('Please select a service');
      return;
    }
    if (serviceType === 'CATALOG' && !selectedCatalogService) {
      setError('Please select a catalog service');
      return;
    }
    if (serviceType === 'CUSTOM' && !customServiceName.trim()) {
      setError('Please enter a service name');
      return;
    }
    if (!customerName.trim()) {
      setError('Please enter the customer name');
      return;
    }
    if (!govtCharge && !serviceCharge) {
      setError('Please enter at least one charge (Government or Service)');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // A catalog (internal) service is billed as an external line named after the internal service —
      // the same shape as a custom line, just pre-filled from the org-resolved catalog price.
      const catalogName = selectedCatalogService?.name || selectedCatalogService?.service_name || '';
      const resolvedName = serviceType === 'THAKLIS'
        ? selectedService?.name
        : (serviceType === 'CATALOG' ? catalogName : customServiceName);
      const serviceData = {
        serviceType: serviceType === 'CATALOG' ? 'CUSTOM' : serviceType,
        thaklisServiceId: serviceType === 'THAKLIS' ? selectedService?.id : null,
        internalServiceId: serviceType === 'CATALOG' ? selectedCatalogService?.id : null,
        customServiceName: serviceType === 'THAKLIS' ? null : resolvedName,
        serviceName: resolvedName,
        customerName: customerName.trim(),
        transactionNumber: transactionNumber.trim() || null,
        govtCharge: parseFloat(govtCharge) || 0,
        serviceCharge: parseFloat(serviceChargeExclTax) || 0,
        tax: parseFloat(serviceChargeTax) || 0,
        reference: reference.trim() || null,
        quantity: qtyNum,
        description: description.trim() || null
      };

      const response = await ApiService.addExternalService(invoiceId, serviceData);

      if (response.data.success) {
        onSuccess(response.data);
        handleClose();
      } else {
        setError(response.data.message || 'Failed to add external service');
      }
    } catch (err) {
      console.error('Error adding external service:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add external service');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
          Add External Service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
          Add a service performed outside Thaklis to this invoice
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Service Type Selection. "Select from Thaklis Services" is hidden when Thaklis is disabled. */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
            Service Type
          </FormLabel>
          <RadioGroup
            value={serviceType}
            onChange={handleServiceTypeChange}
          >
            {!thaklisDisabled && (
              <FormControlLabel
                value="THAKLIS"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Select from Thaklis Services</Typography>}
                sx={{ mb: 0.5 }}
              />
            )}
            <FormControlLabel
              value="CATALOG"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Add from Catalog (Internal Services)</Typography>}
              sx={{ mb: 0.5 }}
            />
            <FormControlLabel
              value="CUSTOM"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Enter Custom Service</Typography>}
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Service Selection (Thaklis) */}
        {serviceType === 'THAKLIS' && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                value={selectedServiceGroup}
                onChange={(e, newValue) => {
                  setSelectedServiceGroup(newValue);
                  setSelectedService(null);
                }}
                options={serviceGroups}
                getOptionLabel={(option) => option.name || option.value || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service Group"
                    placeholder="Filter by group (optional)"
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  />
                )}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                value={selectedService}
                onChange={(e, newValue) => setSelectedService(newValue)}
                options={filteredServices}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service *"
                    placeholder="Search and select service"
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Govt: AED {option.govtCharge?.toFixed(2)} | Service: AED {option.serviceCharge?.toFixed(2)}
                      </Typography>
                    </Box>
                  </li>
                )}
                loading={loading}
              />
            </Grid>
          </Grid>
        )}

        {/* Catalog (internal) service selection — org-resolved prices */}
        {serviceType === 'CATALOG' && (
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              options={catalogServices}
              getOptionLabel={(option) => option.name || option.service_name || ''}
              value={selectedCatalogService}
              onChange={(e, newValue) => setSelectedCatalogService(newValue)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Catalog Service *" placeholder="Select an internal service..." />
              )}
              noOptionsText="No internal services available for this client"
            />
          </Box>
        )}

        {/* Custom Service Name */}
        {serviceType === 'CUSTOM' && (
          <TextField
            fullWidth
            size="small"
            label="Service Name *"
            value={customServiceName}
            onChange={(e) => setCustomServiceName(e.target.value)}
            placeholder="Enter the service name"
            InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
            sx={{ mb: 2 }}
          />
        )}

        {/* Customer Name */}
        <TextField
          fullWidth
          size="small"
          label="Customer/Employee Name *"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Name of the person this service is for"
          InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
          sx={{ mb: 2 }}
        />

        {/* Transaction Number */}
        <TextField
          fullWidth
          size="small"
          label="Transaction Number (Optional)"
          value={transactionNumber}
          onChange={(e) => setTransactionNumber(e.target.value)}
          placeholder="e.g., GDRFA/ICP transaction number"
          helperText="Up to 3, separated by commas"
          InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
          sx={{ mb: 2 }}
        />

        {/* Reference (Optional) */}
        <TextField
          fullWidth
          size="small"
          label="Reference (Optional)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Your internal tracking number"
          InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
          sx={{ mb: 2 }}
        />

        {/* Description (Optional) */}
        <TextField
          fullWidth
          size="small"
          label="Description (Optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Shown under the service name on the invoice"
          multiline
          minRows={1}
          InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Financial Details */}
        <Typography variant="body2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
          Financial Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 0.5 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              helperText="Whole number, min 1 — charges above are per unit"
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Government Charge"
              type="number"
              value={govtCharge}
              onChange={(e) => setGovtCharge(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Typography variant="caption">AED</Typography></InputAdornment>
              }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Service Charge (Incl. Tax)"
              type="number"
              value={serviceCharge}
              onChange={(e) => {
                const value = e.target.value;
                setServiceCharge(value);

                const inclValue = parseFloat(value);
                if (!Number.isFinite(inclValue) || inclValue <= 0) {
                  setServiceChargeExclTax('');
                  setServiceChargeTax('');
                  return;
                }

                const exclRounded = Math.round((inclValue / 1.05) * 100) / 100;
                const taxRounded = Math.round((inclValue - exclRounded) * 100) / 100;
                setServiceChargeExclTax(exclRounded.toFixed(2));
                setServiceChargeTax(taxRounded.toFixed(2));
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Typography variant="caption">AED</Typography></InputAdornment>
              }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Service Charge (Excl. Tax)"
              type="number"
              value={serviceChargeExclTax}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start"><Typography variant="caption">AED</Typography></InputAdornment>
              }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Tax"
              type="number"
              value={serviceChargeTax}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start"><Typography variant="caption">AED</Typography></InputAdornment>
              }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
            />
          </Grid>
        </Grid>

        {/* Tax & Total Summary */}
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>
              Total:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="primary">
              AED {total.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.7rem' }}>
          * Service charge includes 5% VAT
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ fontSize: '0.875rem' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting && <CircularProgress size={16} color="inherit" />}
          sx={{ fontSize: '0.875rem', px: 3 }}
        >
          {submitting ? 'Adding...' : 'Add to Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExternalServiceDialog;
